import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { S, Field, Select, Modal, SearchSelect, itemOptions, fmtQty, fmtMoney, fmtDate, today } from './StoresShared';
import { Vendors, PurchaseOrders, GoodsReceipts, BillsPayments } from './StoresPurchase';
import { Indents, IssuesReturns, Transfers, WastageAdjust, PhysicalCount } from './StoresMovements';
import { PurchaseRequests, Approvals, ApprovalRules } from './StoresRequests';
import { Linen, RoomConsumption, PmKits, FuelLog, ModuleMoves } from './StoresOps';
import StoresReports from './StoresReports';

// Stores module — Phase 1 (masters + stock engine), Phase 2a purchasing
// (StoresPurchase.js), Phase 2b stock movements (StoresMovements.js) and
// Phase 2c purchase requests / approvals (StoresRequests.js) and Phase 3
// housekeeping / maintenance (StoresOps.js).
// One screen component for every "stores-*" menu key; `view` picks the page.
// API: /api/v1/stores (backend/v1/routes/stores.js). Stock only moves through
// documents posted on the server — this screen never edits a balance directly.

const ROLE_LABELS = {
  STORE_MANAGER: 'Store Manager',
  STOREKEEPER: 'Storekeeper',
  PURCHASE_STAFF: 'Purchase Staff',
  PURCHASE_MANAGER: 'Purchase Manager',
  ACCOUNTS: 'Accounts',
  DEPT_HEAD: 'Department Head',
  REQUESTER: 'Requester',
  COST_CONTROLLER: 'Cost Controller',
};
const TXN_LABELS = {
  OPENING: 'Opening', OVERRIDE_IN: 'Override (admin)', RECEIPT: 'Receipt', GRN: 'GRN', GRN_CANCEL: 'GRN cancelled', ISSUE: 'Issue',
  COUNT_GAIN: 'Count gain', COUNT_LOSS: 'Count loss', MIGRATION: 'Brought over', ADJUSTMENT: 'Adjustment',
  LINEN_ISSUE: 'Linen out', LINEN_RETURN: 'Linen back', ROOM_CONSUMPTION: 'Room consumption',
  RETURN: 'Return', TRANSFER_IN: 'Transfer in', TRANSFER_OUT: 'Transfer out', ADJUST_IN: 'Adjust +',
  ADJUST_OUT: 'Adjust −', WASTAGE: 'Wastage', CONSUMPTION: 'Consumption', PURCHASE_RETURN: 'Purchase return',
};

const PAGE_TITLES = {
  'stores-stock': ['Stock on Hand', 'What is in each store right now, with value and reorder status.'],
  'stores-ledger': ['Item Ledger', 'Every movement of an item, newest first.'],
  'stores-opening': ['Opening Stock', 'Bring existing stock into a store. Each entry posts immediately.'],
  'stores-items': ['Items', 'One item list for every department, with units and stock levels.'],
  'stores-masters': ['Stores & Masters', 'Stores, categories, units and departments.'],
  'stores-settings': ['Settings & Access', 'Who can use Stores, document numbering and the override register.'],
  'stores-po': ['Purchase Orders', 'Order from vendors. A Purchase Manager approves before goods are received.'],
  'stores-grn': ['Goods Receipt (GRN)', 'Receive goods into a store, with or without a purchase order. Creates the vendor bill.'],
  'stores-bills': ['Vendor Bills & Payments', 'Bills from goods received, payments to vendors, and what is outstanding.'],
  'stores-vendors': ['Vendors', 'Suppliers for every department, with GSTIN, credit days and outstanding.'],
  'stores-indents': ['Indents', 'Departments ask a store for items; the store issues against the indent.'],
  'stores-issues': ['Issue & Return', 'Stock going out to kitchens, departments, rooms or work orders, returns, and consumption.'],
  'stores-transfers': ['Transfers', 'Move stock between stores of the same department.'],
  'stores-wastage': ['Wastage & Adjustments', 'Write off expired or damaged stock, and correct stock with a reason.'],
  'stores-count': ['Physical Count', 'Count a store and post the difference as gain or loss.'],
  'stores-pr': ['Purchase Requests', 'Ask Purchase to buy. Approved requests become purchase orders.'],
  'stores-approvals': ['Approvals', 'Purchase requests and purchase orders waiting for your approval.'],
  'stores-linen': ['Linen', 'Linen in store, in use, at the laundry and damaged — with par levels and losses.'],
  'stores-room-consumption': ['Room Consumption', 'Guest amenities used per room, posted from housekeeping, with any shortages.'],
  'stores-pm-kits': ['PM Kits', 'Spares and consumables issued together for preventive-maintenance jobs.'],
  'stores-fuel': ['Fuel Log', 'Fuel issued to generators, boilers and vehicles, with consumption per hour or km.'],
  'stores-reports': ['Reports', 'Stock, movement, purchase and vendor reports for every Stores menu, with Excel and print.'],
};

export default function StoresModule({ token, selectedProperty, view }) {
  const [boot, setBoot] = useState(null);
  const [bootErr, setBootErr] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [ledgerItem, setLedgerItem] = useState(null);

  const api = useCallback(async (path, opts = {}) => {
    const res = await fetch(`/api/v1/stores${path}`, {
      method: opts.method || 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-chain-id': selectedProperty?.chain_id || '',
        'x-property-id': selectedProperty?.id || '',
      },
      body: opts.body ? JSON.stringify(opts.body) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const e = new Error(data.error || `Request failed (HTTP ${res.status})`);
      e.details = data.details;
      throw e;
    }
    return data;
  }, [token, selectedProperty]);

  const reloadBoot = useCallback(() => {
    if (!selectedProperty?.id) return;
    setBootErr('');
    api('/bootstrap').then(setBoot).catch((e) => setBootErr(e.message));
  }, [api, selectedProperty]);

  useEffect(() => { setBoot(null); reloadBoot(); }, [reloadBoot]);

  // File upload / download for the Excel import (multipart, so no JSON content type).
  const io = useMemo(() => {
    const scopeHeaders = {
      Authorization: `Bearer ${token}`,
      'x-chain-id': selectedProperty?.chain_id || '',
      'x-property-id': selectedProperty?.id || '',
    };
    return {
      upload: async (path, form) => {
        const res = await fetch(`/api/v1/stores${path}`, { method: 'POST', headers: scopeHeaders, body: form });
        const data = await res.json().catch(() => ({}));
        if (!res.ok && !data.rows) throw new Error(data.error || `Upload failed (HTTP ${res.status})`);
        return { ok: res.ok, ...data };
      },
      download: async (path, filename) => {
        const res = await fetch(`/api/v1/stores${path}`, { headers: scopeHeaders });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || `Download failed (HTTP ${res.status})`);
        }
        const url = URL.createObjectURL(await res.blob());
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      },
    };
  }, [token, selectedProperty]);

  const [title, subtitle] = PAGE_TITLES[view] || PAGE_TITLES['stores-stock'];

  if (!selectedProperty?.id) return <div style={S.page}><div style={S.empty}>Select a property to open Stores.</div></div>;

  const openLedger = (item) => { setLedgerItem(item); };

  return (
    <div style={S.page}>
      <div style={S.head}>
        <div>
          <h2 style={S.h2}>{title}</h2>
          <p style={S.sub}>{subtitle}</p>
        </div>
        {boot && ['stores-stock', 'stores-items', 'stores-opening'].includes(view) && (
          <Field label="Department">
            <Select style={{ ...S.inp, minWidth: 180 }} value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)}>
              <option value="">All departments</option>
              {boot.departments.filter((d) => d.is_active).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </Select>
          </Field>
        )}
      </div>
      {bootErr && <div style={S.err}>{bootErr}</div>}
      {!boot && !bootErr && <div style={S.empty}>Loading…</div>}
      {boot && view === 'stores-stock' && !ledgerItem && <StockOnHand api={api} boot={boot} deptFilter={deptFilter} onLedger={openLedger} />}
      {boot && view === 'stores-stock' && ledgerItem && (
        <div>
          <button type="button" className="btn-muted btn-sm" onClick={() => setLedgerItem(null)} style={{ marginBottom: '0.75rem' }}>← Back to stock</button>
          <ItemLedger api={api} boot={boot} initialItem={ledgerItem} />
        </div>
      )}
      {boot && view === 'stores-ledger' && <ItemLedger api={api} boot={boot} initialItem={ledgerItem} />}
      {boot && view === 'stores-opening' && <OpeningStock api={api} io={io} boot={boot} deptFilter={deptFilter} />}
      {boot && view === 'stores-items' && <Items api={api} io={io} boot={boot} deptFilter={deptFilter} onChanged={reloadBoot} />}
      {boot && view === 'stores-masters' && <Masters api={api} boot={boot} onChanged={reloadBoot} />}
      {boot && view === 'stores-settings' && <SettingsAccess api={api} boot={boot} onChanged={reloadBoot} />}
      {boot && view === 'stores-po' && <PurchaseOrders api={api} boot={boot} property={selectedProperty} />}
      {boot && view === 'stores-grn' && <GoodsReceipts api={api} boot={boot} />}
      {boot && view === 'stores-bills' && <BillsPayments api={api} boot={boot} />}
      {boot && view === 'stores-vendors' && <Vendors api={api} boot={boot} />}
      {boot && view === 'stores-indents' && <Indents api={api} boot={boot} />}
      {boot && view === 'stores-issues' && <IssuesReturns api={api} boot={boot} />}
      {boot && view === 'stores-transfers' && <Transfers api={api} boot={boot} />}
      {boot && view === 'stores-wastage' && <WastageAdjust api={api} boot={boot} />}
      {boot && view === 'stores-count' && <PhysicalCount api={api} boot={boot} />}
      {boot && view === 'stores-pr' && <PurchaseRequests api={api} boot={boot} />}
      {boot && view === 'stores-approvals' && <Approvals api={api} boot={boot} />}
      {boot && view === 'stores-linen' && <Linen api={api} boot={boot} />}
      {boot && view === 'stores-room-consumption' && <RoomConsumption api={api} boot={boot} />}
      {boot && view === 'stores-pm-kits' && <PmKits api={api} boot={boot} />}
      {boot && view === 'stores-fuel' && <FuelLog api={api} boot={boot} />}
      {boot && view === 'stores-reports' && <StoresReports api={api} io={io} boot={boot} property={selectedProperty} />}
    </div>
  );
}

// ── Stock on hand ─────────────────────────────────────────────────────────
function StockOnHand({ api, boot, deptFilter, onLedger }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [storeId, setStoreId] = useState('');
  const [q, setQ] = useState('');
  const [belowReorder, setBelowReorder] = useState(false);
  const [includeZero, setIncludeZero] = useState(false);
  const [batchFor, setBatchFor] = useState(null);
  const [batches, setBatches] = useState([]);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (deptFilter) p.set('department_id', deptFilter);
    if (storeId) p.set('store_id', storeId);
    if (q.trim()) p.set('q', q.trim());
    if (belowReorder) p.set('below_reorder', '1');
    if (includeZero) p.set('include_zero', '1');
    setLoading(true); setErr('');
    api(`/stock?${p.toString()}`).then(setRows).catch((e) => setErr(e.message)).finally(() => setLoading(false));
  }, [api, deptFilter, storeId, q, belowReorder, includeZero]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const openBatches = (r) => {
    setBatchFor(r); setBatches([]);
    api(`/stock/batches?store_id=${r.store_id}&item_id=${r.item_id}`).then(setBatches).catch((e) => setErr(e.message));
  };

  const stores = boot.stores.filter((s) => !deptFilter || s.department_id === deptFilter);
  const totalValue = rows.reduce((s, r) => s + Number(r.stock_value || 0), 0);
  const lowCount = rows.filter((r) => Number(r.reorder_qty) > 0 && Number(r.on_hand_qty) <= Number(r.reorder_qty)).length;
  const soon = new Date(); soon.setDate(soon.getDate() + 30);

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
        <div style={S.card}><div style={S.lbl}>Stock value</div><div style={{ fontSize: '1.35rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(totalValue)}</div></div>
        <div style={S.card}><div style={S.lbl}>Lines shown</div><div style={{ fontSize: '1.35rem', fontWeight: 800 }}>{rows.length}</div></div>
        <div style={S.card}><div style={S.lbl}>At or below reorder</div><div style={{ fontSize: '1.35rem', fontWeight: 800, color: lowCount ? '#b45309' : '#15803d' }}>{lowCount}</div></div>
      </div>
      <div style={S.bar}>
        <Field label="Store">
          <Select style={{ ...S.inp, minWidth: 180 }} value={storeId} onChange={(e) => setStoreId(e.target.value)}>
            <option value="">All stores</option>
            {stores.map((s) => <option key={s.id} value={s.id}>{s.store_name}</option>)}
          </Select>
        </Field>
        <Field label="Search"><input style={{ ...S.inp, width: 220 }} value={q} placeholder="Item name or code" onChange={(e) => setQ(e.target.value)} /></Field>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.85rem', paddingBottom: 6 }}><input type="checkbox" checked={belowReorder} onChange={(e) => setBelowReorder(e.target.checked)} /> Only at or below reorder</label>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.85rem', paddingBottom: 6 }}><input type="checkbox" checked={includeZero} onChange={(e) => setIncludeZero(e.target.checked)} /> Include zero stock</label>
      </div>
      {err && <div style={S.err}>{err}</div>}
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>Item</th><th style={S.th}>Store</th><th style={S.th}>Department</th>
            <th style={{ ...S.th, ...S.num }}>On hand</th><th style={{ ...S.th, ...S.num }}>Avg cost</th><th style={{ ...S.th, ...S.num }}>Value</th>
            <th style={{ ...S.th, ...S.num }}>Reorder at</th><th style={S.th}>Next expiry</th><th style={S.th} />
          </tr></thead>
          <tbody>
            {rows.map((r) => {
              const low = Number(r.reorder_qty) > 0 && Number(r.on_hand_qty) <= Number(r.reorder_qty);
              const expSoon = r.next_expiry && new Date(r.next_expiry) <= soon;
              return (
                <tr key={`${r.store_id}-${r.item_id}`}>
                  <td style={S.td}><div style={{ fontWeight: 600 }}>{r.item_name}</div><div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{r.item_code}{r.category_name ? ` · ${r.category_name}` : ''}</div></td>
                  <td style={S.td}>{r.store_name}</td>
                  <td style={S.td}>{r.department_name}</td>
                  <td style={{ ...S.td, ...S.num, fontWeight: 700 }}>{fmtQty(r.on_hand_qty)} <span style={{ color: '#94a3b8', fontWeight: 400 }}>{r.base_uom}</span></td>
                  <td style={{ ...S.td, ...S.num }}>{fmtMoney(r.wavg_cost)}</td>
                  <td style={{ ...S.td, ...S.num }}>{fmtMoney(r.stock_value)}</td>
                  <td style={{ ...S.td, ...S.num }}>{Number(r.reorder_qty) > 0 ? <>{fmtQty(r.reorder_qty)} {low && <span style={S.pill('#fef3c7', '#b45309')}>Reorder</span>}</> : '—'}</td>
                  <td style={S.td}>{r.next_expiry ? <span style={expSoon ? S.pill('#fee2e2', '#b91c1c') : undefined}>{fmtDate(r.next_expiry)}</span> : '—'}</td>
                  <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                    <button type="button" className="btn-muted btn-sm" onClick={() => openBatches(r)}>Batches</button>{' '}
                    <button type="button" className="btn-muted btn-sm" onClick={() => onLedger({ id: r.item_id, item_name: r.item_name, item_code: r.item_code, store_id: r.store_id })}>Ledger</button>
                  </td>
                </tr>
              );
            })}
            {!rows.length && <tr><td colSpan={9} style={S.empty}>{loading ? 'Loading…' : 'No stock found. Post opening stock to get started.'}</td></tr>}
          </tbody>
        </table>
      </div>
      {batchFor && (
        <Modal title={`Batches — ${batchFor.item_name} · ${batchFor.store_name}`} onClose={() => setBatchFor(null)} width={720}>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead><tr><th style={S.th}>Batch</th><th style={S.th}>Received</th><th style={S.th}>Expiry</th><th style={{ ...S.th, ...S.num }}>Received qty</th><th style={{ ...S.th, ...S.num }}>Remaining</th><th style={{ ...S.th, ...S.num }}>Cost</th></tr></thead>
              <tbody>
                {batches.map((b) => (
                  <tr key={b.id}>
                    <td style={S.td}>{b.batch_no || '—'}</td><td style={S.td}>{fmtDate(b.received_at)}</td><td style={S.td}>{fmtDate(b.expiry_date)}</td>
                    <td style={{ ...S.td, ...S.num }}>{fmtQty(b.received_qty)}</td><td style={{ ...S.td, ...S.num, fontWeight: 700 }}>{fmtQty(b.remaining_qty)}</td><td style={{ ...S.td, ...S.num }}>{fmtMoney(b.unit_cost)}</td>
                  </tr>
                ))}
                {!batches.length && <tr><td colSpan={6} style={S.empty}>No open batches.</td></tr>}
              </tbody>
            </table>
          </div>
          <p style={{ ...S.sub, marginTop: '0.6rem' }}>Issues take stock from the top batch first (earliest expiry for chilled / frozen items, otherwise oldest receipt).</p>
        </Modal>
      )}
    </>
  );
}

// ── Item ledger ───────────────────────────────────────────────────────────
function ItemLedger({ api, boot, initialItem }) {
  const [items, setItems] = useState([]);
  const [itemId, setItemId] = useState(initialItem?.id || '');
  const [storeId, setStoreId] = useState(initialItem?.store_id || '');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => { api('/items').then(setItems).catch((e) => setErr(e.message)); }, [api]);
  useEffect(() => {
    if (!itemId) { setRows([]); return; }
    const p = new URLSearchParams({ item_id: itemId });
    if (storeId) p.set('store_id', storeId);
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    setLoading(true); setErr('');
    api(`/ledger?${p.toString()}`).then(setRows).catch((e) => setErr(e.message)).finally(() => setLoading(false));
  }, [api, itemId, storeId, from, to]);

  const item = items.find((i) => i.id === itemId);
  const stores = boot.stores.filter((s) => !item || s.department_id === item.department_id);

  return (
    <>
      <div style={S.bar}>
        <Field label="Item">
          <Select style={{ ...S.inp, minWidth: 280 }} value={itemId} onChange={(e) => setItemId(e.target.value)}>
            <option value="">Choose an item…</option>
            {items.map((i) => <option key={i.id} value={i.id}>{i.item_name} ({i.item_code}) · {i.department_name}</option>)}
          </Select>
        </Field>
        <Field label="Store">
          <Select style={{ ...S.inp, minWidth: 170 }} value={storeId} onChange={(e) => setStoreId(e.target.value)}>
            <option value="">All stores</option>
            {stores.map((s) => <option key={s.id} value={s.id}>{s.store_name}</option>)}
          </Select>
        </Field>
        <Field label="From"><input type="date" style={S.inp} value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
        <Field label="To"><input type="date" style={S.inp} value={to} onChange={(e) => setTo(e.target.value)} /></Field>
      </div>
      {err && <div style={S.err}>{err}</div>}
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>Date</th><th style={S.th}>Type</th><th style={S.th}>Document</th><th style={S.th}>Store</th><th style={S.th}>Batch</th>
            <th style={{ ...S.th, ...S.num }}>In</th><th style={{ ...S.th, ...S.num }}>Out</th><th style={{ ...S.th, ...S.num }}>Balance</th>
            <th style={{ ...S.th, ...S.num }}>Cost</th><th style={{ ...S.th, ...S.num }}>Value</th><th style={S.th}>By</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} style={r.is_override ? { background: '#fff7ed' } : undefined}>
                <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{fmtDate(r.txn_date)}</td>
                <td style={S.td}>{TXN_LABELS[r.txn_type] || r.txn_type}{r.is_override && <> <span style={S.pill('#ffedd5', '#c2410c')}>Override</span></>}</td>
                <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{r.doc_no || '—'}</td>
                <td style={S.td}>{r.store_name}</td>
                <td style={S.td}>{r.batch_no || '—'}{r.expiry_date ? <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>exp {fmtDate(r.expiry_date)}</div> : null}</td>
                <td style={{ ...S.td, ...S.num, color: '#15803d' }}>{Number(r.qty_in) ? fmtQty(r.qty_in) : ''}</td>
                <td style={{ ...S.td, ...S.num, color: '#b91c1c' }}>{Number(r.qty_out) ? fmtQty(r.qty_out) : ''}</td>
                <td style={{ ...S.td, ...S.num, fontWeight: 700 }}>{r.balance_after != null ? fmtQty(r.balance_after) : '—'}</td>
                <td style={{ ...S.td, ...S.num }}>{fmtMoney(r.unit_cost)}</td>
                <td style={{ ...S.td, ...S.num }}>{fmtMoney(r.value)}</td>
                <td style={S.td}>{r.posted_by || '—'}{r.remarks ? <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{r.remarks}</div> : null}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={11} style={S.empty}>{loading ? 'Loading…' : itemId ? 'No movements for this item yet.' : 'Choose an item to see its movements.'}</td></tr>}
          </tbody>
        </table>
      </div>
      {item && <p style={{ ...S.sub, marginTop: '0.5rem' }}>Quantities are in {item.base_uom}. "Balance" is the store's balance after each movement.</p>}
    </>
  );
}

// ── Opening stock ─────────────────────────────────────────────────────────
const blankLine = () => ({ key: Math.random().toString(36).slice(2), item_id: '', uom_code: '', qty: '', rate: '', batch_no: '', expiry_date: '' });

function OpeningStock({ api, io, boot, deptFilter }) {
  const [showImport, setShowImport] = useState(false);
  const [docs, setDocs] = useState([]);
  const [items, setItems] = useState([]);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [viewDoc, setViewDoc] = useState(null);
  const canPost = boot.me.is_hotel_admin || boot.me.roles.includes('STORE_MANAGER');

  const loadDocs = useCallback(() => {
    const p = new URLSearchParams({ doc_type: 'OPENING' });
    if (deptFilter) p.set('department_id', deptFilter);
    api(`/docs?${p.toString()}`).then(setDocs).catch((e) => setErr(e.message));
  }, [api, deptFilter]);
  useEffect(() => { loadDocs(); }, [loadDocs]);
  useEffect(() => { api('/items?active=1').then(setItems).catch((e) => setErr(e.message)); }, [api]);

  const stores = boot.stores.filter((s) => s.is_active && (!deptFilter || s.department_id === deptFilter));
  const store = form ? boot.stores.find((s) => s.id === form.store_id) : null;
  const storeItems = store ? items.filter((i) => i.department_id === store.department_id) : [];
  const itemById = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);

  const setLine = (key, patch) => setForm((f) => ({ ...f, lines: f.lines.map((l) => (l.key === key ? { ...l, ...patch } : l)) }));
  const lineTotal = (l) => (Number(l.qty) || 0) * (Number(l.rate) || 0);
  const total = form ? form.lines.reduce((s, l) => s + lineTotal(l), 0) : 0;

  const post = async () => {
    setSaving(true); setErr(''); setMsg('');
    try {
      // Expiry is optional on opening stock: a physical count rarely has the dates.
      const lines = form.lines.filter((l) => l.item_id && Number(l.qty) > 0);
      const doc = await api('/opening', { method: 'POST', body: { store_id: form.store_id, doc_date: form.doc_date, remarks: form.remarks, lines } });
      setMsg(`Posted ${doc.doc_no} — ${fmtMoney(doc.grand_total)} added to ${store?.store_name}.`);
      setForm(null);
      loadDocs();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const openDoc = (id) => api(`/docs/${id}`).then(setViewDoc).catch((e) => setErr(e.message));

  return (
    <>
      {err && <div style={S.err}>{err}</div>}
      {msg && <div style={S.ok}>{msg}</div>}
      {canPost && !form && (
        <div style={{ marginBottom: '0.9rem' }}>
          <button type="button" className="btn-primary" onClick={() => { setErr(''); setMsg(''); setForm({ store_id: stores[0]?.id || '', doc_date: today(), remarks: '', lines: [blankLine()] }); }} disabled={!stores.length}>+ New opening stock</button>{' '}
          <button type="button" className="btn-muted" onClick={() => { setErr(''); setMsg(''); setShowImport(true); }} disabled={!stores.length}>Import from Excel</button>
          {!stores.length && <span style={{ ...S.sub, marginLeft: 8 }}>Create a store first (Stores &amp; Masters).</span>}
        </div>
      )}
      {showImport && (
        <ImportDialog kind="opening" io={io} boot={boot} deptFilter={deptFilter} onClose={() => setShowImport(false)}
          onDone={(m) => { setMsg(m); loadDocs(); }} />
      )}
      {form && (
        <div style={S.card}>
          <div style={S.bar}>
            <Field label="Store">
              <Select style={{ ...S.inp, minWidth: 220 }} value={form.store_id} onChange={(e) => setForm({ ...form, store_id: e.target.value, lines: [blankLine()] })}>
                {stores.map((s) => <option key={s.id} value={s.id}>{s.store_name}</option>)}
              </Select>
            </Field>
            <Field label="Date"><input type="date" style={S.inp} value={form.doc_date} onChange={(e) => setForm({ ...form, doc_date: e.target.value })} /></Field>
            <Field label="Remarks" style={{ flex: 1, minWidth: 200 }}><input style={{ ...S.inp, width: '100%' }} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} placeholder="e.g. Stock taken on go-live day" /></Field>
          </div>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead><tr>
                <th style={S.th}>Item</th><th style={S.th}>Unit</th><th style={{ ...S.th, ...S.num }}>Qty</th><th style={{ ...S.th, ...S.num }}>Rate / unit</th>
                <th style={S.th}>Batch</th><th style={S.th}>Expiry</th><th style={{ ...S.th, ...S.num }}>Amount</th><th style={S.th} />
              </tr></thead>
              <tbody>
                {form.lines.map((l) => {
                  const it = itemById[l.item_id];
                  const units = it ? [it.base_uom, ...(it.uoms || []).map((u) => u.uom_code)] : [];
                  return (
                    <tr key={l.key}>
                      <td style={S.td}>
                        <SearchSelect style={{ minWidth: 220 }} value={l.item_id} options={itemOptions(storeItems)} onChange={(v) => { const ni = itemById[v]; setLine(l.key, { item_id: v, uom_code: ni?.base_uom || '', rate: ni && Number(ni.standard_cost) ? String(ni.standard_cost) : l.rate }); }} />
                      </td>
                      <td style={S.td}>
                        <Select style={{ ...S.inp, width: 90 }} value={l.uom_code} onChange={(e) => setLine(l.key, { uom_code: e.target.value })} disabled={!it}>
                          {units.map((u) => <option key={u} value={u}>{u}</option>)}
                        </Select>
                      </td>
                      <td style={S.td}><input type="number" min="0" step="any" style={{ ...S.inp, width: 90, textAlign: 'right' }} value={l.qty} onChange={(e) => setLine(l.key, { qty: e.target.value })} /></td>
                      <td style={S.td}><input type="number" min="0" step="any" style={{ ...S.inp, width: 100, textAlign: 'right' }} value={l.rate} onChange={(e) => setLine(l.key, { rate: e.target.value })} /></td>
                      <td style={S.td}><input style={{ ...S.inp, width: 110 }} value={l.batch_no} onChange={(e) => setLine(l.key, { batch_no: e.target.value })} disabled={!it?.batch_tracked} placeholder={it?.batch_tracked ? 'Batch no.' : '—'} /></td>
                      <td style={S.td}><input type="date" style={S.inp} value={l.expiry_date} onChange={(e) => setLine(l.key, { expiry_date: e.target.value })} disabled={!it?.expiry_tracked} /></td>
                      <td style={{ ...S.td, ...S.num }}>{fmtMoney(lineTotal(l))}</td>
                      <td style={S.td}><button type="button" className="btn-muted btn-sm" onClick={() => setForm((f) => ({ ...f, lines: f.lines.length > 1 ? f.lines.filter((x) => x.key !== l.key) : [blankLine()] }))} aria-label="Remove line">✕</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {store && !storeItems.length && <p style={{ ...S.sub, marginTop: '0.5rem' }}>No active items in this store's department yet. Add them under Items.</p>}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
            <button type="button" className="btn-muted btn-sm" onClick={() => setForm((f) => ({ ...f, lines: [...f.lines, blankLine()] }))}>+ Add line</button>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <strong style={{ fontVariantNumeric: 'tabular-nums' }}>Total {fmtMoney(total)}</strong>
              <button type="button" className="btn-muted" onClick={() => setForm(null)} disabled={saving}>Cancel</button>
              <button type="button" className="btn-primary" onClick={post} disabled={saving || !form.store_id}>{saving ? 'Posting…' : 'Post opening stock'}</button>
            </div>
          </div>
          <p style={{ ...S.sub, marginTop: '0.5rem' }}>Posting adds the stock immediately and cannot be edited afterwards; corrections are made with adjustments.</p>
        </div>
      )}
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr><th style={S.th}>Doc no.</th><th style={S.th}>Date</th><th style={S.th}>Store</th><th style={S.th}>Department</th><th style={{ ...S.th, ...S.num }}>Lines</th><th style={{ ...S.th, ...S.num }}>Value</th><th style={S.th}>Posted by</th><th style={S.th} /></tr></thead>
          <tbody>
            {docs.map((d) => (
              <tr key={d.id}>
                <td style={{ ...S.td, fontWeight: 600, whiteSpace: 'nowrap' }}>{d.doc_no}</td><td style={S.td}>{fmtDate(d.doc_date)}</td><td style={S.td}>{d.to_store_name}</td><td style={S.td}>{d.department_name}</td>
                <td style={{ ...S.td, ...S.num }}>{d.line_count}</td><td style={{ ...S.td, ...S.num }}>{fmtMoney(d.grand_total)}</td><td style={S.td}>{d.posted_by || '—'}</td>
                <td style={S.td}><button type="button" className="btn-muted btn-sm" onClick={() => openDoc(d.id)}>View</button></td>
              </tr>
            ))}
            {!docs.length && <tr><td colSpan={8} style={S.empty}>No opening stock posted yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {viewDoc && (
        <Modal title={`${viewDoc.doc_no} · ${viewDoc.to_store_name}`} onClose={() => setViewDoc(null)}>
          <p style={{ ...S.sub, marginTop: 0 }}>{fmtDate(viewDoc.doc_date)} · posted by {viewDoc.posted_by || '—'}{viewDoc.remarks ? ` · ${viewDoc.remarks}` : ''}</p>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead><tr><th style={S.th}>#</th><th style={S.th}>Item</th><th style={{ ...S.th, ...S.num }}>Qty</th><th style={{ ...S.th, ...S.num }}>Base qty</th><th style={{ ...S.th, ...S.num }}>Rate</th><th style={S.th}>Batch / expiry</th><th style={{ ...S.th, ...S.num }}>Amount</th></tr></thead>
              <tbody>
                {viewDoc.lines.map((l) => (
                  <tr key={l.id}>
                    <td style={S.td}>{l.line_no}</td><td style={S.td}>{l.item_name}<div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{l.item_code}</div></td>
                    <td style={{ ...S.td, ...S.num }}>{fmtQty(l.qty)} {l.uom_code}</td><td style={{ ...S.td, ...S.num }}>{fmtQty(l.base_qty)} {l.base_uom}</td>
                    <td style={{ ...S.td, ...S.num }}>{fmtMoney(l.rate)}</td><td style={S.td}>{l.batch_no || '—'}{l.expiry_date ? ` · ${fmtDate(l.expiry_date)}` : ''}</td>
                    <td style={{ ...S.td, ...S.num }}>{fmtMoney(l.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ textAlign: 'right', marginTop: '0.6rem', fontWeight: 800 }}>Total {fmtMoney(viewDoc.grand_total)}</div>
        </Modal>
      )}
    </>
  );
}

// ── Items ─────────────────────────────────────────────────────────────────
const emptyItem = (deptId) => ({
  id: null, department_id: deptId || '', category_id: '', item_code: '', item_name: '', item_type: 'CONSUMABLE', base_uom: 'NOS',
  hsn_code: '', gst_rate: 0, storage_type: 'DRY', valuation: 'FIFO', batch_tracked: false, expiry_tracked: false,
  standard_cost: 0, description: '', is_active: true, uoms: [], stock_levels: [], has_movements: false,
});

function Items({ api, io, boot, deptFilter, onChanged }) {
  const [showImport, setShowImport] = useState(false);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const canEdit = boot.me.is_hotel_admin || boot.me.roles.includes('STORE_MANAGER');

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (deptFilter) p.set('department_id', deptFilter);
    if (q.trim()) p.set('q', q.trim());
    api(`/items?${p.toString()}`).then(setRows).catch((e) => setErr(e.message));
  }, [api, deptFilter, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const openEdit = async (id) => {
    setErr(''); setMsg('');
    if (!id) { setEdit(emptyItem(deptFilter || boot.departments[0]?.id)); return; }
    try {
      const it = await api(`/items/${id}`);
      setEdit({ ...it, category_id: it.category_id || '', hsn_code: it.hsn_code || '', description: it.description || '' });
    } catch (e) { setErr(e.message); }
  };

  const save = async () => {
    setSaving(true); setErr('');
    try {
      const body = { ...edit, uoms: edit.uoms.filter((u) => u.uom_code && Number(u.factor_to_base) > 0) };
      const saved = edit.id ? await api(`/items/${edit.id}`, { method: 'PUT', body }) : await api('/items', { method: 'POST', body });
      setMsg(`${saved.item_name} (${saved.item_code}) saved.`);
      setEdit(null);
      load();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const set = (patch) => setEdit((e) => ({ ...e, ...patch }));
  const deptStores = edit ? boot.stores.filter((s) => s.department_id === edit.department_id) : [];
  const levelFor = (storeId) => edit.stock_levels.find((l) => l.store_id === storeId) || { store_id: storeId, min_qty: 0, reorder_qty: 0, max_qty: 0, par_qty: 0 };
  const setLevel = (storeId, patch) => setEdit((e) => {
    const others = e.stock_levels.filter((l) => l.store_id !== storeId);
    return { ...e, stock_levels: [...others, { ...levelFor(storeId), ...patch }] };
  });

  return (
    <>
      <div style={S.bar}>
        <Field label="Search"><input style={{ ...S.inp, width: 240 }} value={q} placeholder="Item name or code" onChange={(e) => setQ(e.target.value)} /></Field>
        {canEdit && <button type="button" className="btn-primary" onClick={() => openEdit(null)}>+ New item</button>}
        {canEdit && <button type="button" className="btn-muted" onClick={() => { setErr(''); setMsg(''); setShowImport(true); }}>Import from Excel</button>}
      </div>
      {showImport && (
        <ImportDialog kind="items" io={io} boot={boot} deptFilter={deptFilter} onClose={() => setShowImport(false)}
          onDone={(m) => { setMsg(m); load(); onChanged(); }} />
      )}
      {err && !edit && <div style={S.err}>{err}</div>}
      {msg && <div style={S.ok}>{msg}</div>}
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>Code</th><th style={S.th}>Item</th><th style={S.th}>Department</th><th style={S.th}>Category</th><th style={S.th}>Type</th>
            <th style={S.th}>Unit</th><th style={{ ...S.th, ...S.num }}>GST %</th><th style={{ ...S.th, ...S.num }}>On hand</th><th style={{ ...S.th, ...S.num }}>Value</th><th style={S.th}>Status</th><th style={S.th} />
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} style={r.is_active ? undefined : { opacity: 0.55 }}>
                <td style={{ ...S.td, whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{r.item_code}</td>
                <td style={{ ...S.td, fontWeight: 600 }}>{r.item_name}{(r.batch_tracked || r.expiry_tracked) && <> <span style={S.pill('#e0f2fe', '#0369a1')}>{r.expiry_tracked ? 'Expiry' : 'Batch'}</span></>}</td>
                <td style={S.td}>{r.department_name}</td><td style={S.td}>{r.category_name || '—'}</td><td style={S.td}>{r.item_type}</td>
                <td style={S.td}>{r.base_uom}{(r.uoms || []).length ? <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{r.uoms.map((u) => `${u.uom_code}=${fmtQty(u.factor_to_base)}`).join(', ')}</div> : null}</td>
                <td style={{ ...S.td, ...S.num }}>{fmtQty(r.gst_rate)}</td>
                <td style={{ ...S.td, ...S.num }}>{fmtQty(r.on_hand_qty)}</td><td style={{ ...S.td, ...S.num }}>{fmtMoney(r.stock_value)}</td>
                <td style={S.td}>{r.is_active ? <span style={S.pill('#dcfce7', '#15803d')}>Active</span> : <span style={S.pill('#f1f5f9', '#64748b')}>Inactive</span>}</td>
                <td style={S.td}>{canEdit && <button type="button" className="btn-muted btn-sm" onClick={() => openEdit(r.id)}>Edit</button>}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={11} style={S.empty}>No items yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {edit && (
        <Modal title={edit.id ? `Edit ${edit.item_name}` : 'New item'} onClose={() => setEdit(null)}>
          {err && <div style={S.err}>{err}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '0.75rem' }}>
            <Field label="Department *">
              <Select style={{ ...S.inp, width: '100%' }} value={edit.department_id} disabled={edit.has_movements} onChange={(e) => set({ department_id: e.target.value, category_id: '', stock_levels: [] })}>
                <option value="">Choose…</option>
                {boot.departments.filter((d) => d.is_active).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </Select>
            </Field>
            <Field label="Category">
              <Select style={{ ...S.inp, width: '100%' }} value={edit.category_id} onChange={(e) => set({ category_id: e.target.value })}>
                <option value="">None</option>
                {boot.categories.filter((c) => c.department_id === edit.department_id && c.is_active).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </Field>
            <Field label="Item code"><input style={{ ...S.inp, width: '100%' }} value={edit.item_code} placeholder="Leave blank to auto-number" onChange={(e) => set({ item_code: e.target.value })} /></Field>
            <Field label="Item name *" style={{ gridColumn: 'span 2' }}><input style={{ ...S.inp, width: '100%' }} value={edit.item_name} onChange={(e) => set({ item_name: e.target.value })} /></Field>
            <Field label="Type">
              <Select style={{ ...S.inp, width: '100%' }} value={edit.item_type} onChange={(e) => set({ item_type: e.target.value })}>
                {boot.item_types.map((t) => <option key={t} value={t}>{t}</option>)}
              </Select>
            </Field>
            <Field label="Base unit *">
              <Select style={{ ...S.inp, width: '100%' }} value={edit.base_uom} disabled={edit.has_movements} onChange={(e) => set({ base_uom: e.target.value })}>
                {boot.uoms.filter((u) => u.is_active || u.code === edit.base_uom).map((u) => <option key={u.code} value={u.code}>{u.code} — {u.name}</option>)}
              </Select>
            </Field>
            <Field label="HSN / SAC"><input style={{ ...S.inp, width: '100%' }} value={edit.hsn_code} onChange={(e) => set({ hsn_code: e.target.value })} /></Field>
            <Field label="GST %"><input type="number" min="0" max="40" step="any" style={{ ...S.inp, width: '100%' }} value={edit.gst_rate} onChange={(e) => set({ gst_rate: e.target.value })} /></Field>
            <Field label="Standard cost / base unit"><input type="number" min="0" step="any" style={{ ...S.inp, width: '100%' }} value={edit.standard_cost} onChange={(e) => set({ standard_cost: e.target.value })} /></Field>
            <Field label="Storage">
              <Select style={{ ...S.inp, width: '100%' }} value={edit.storage_type} onChange={(e) => set({ storage_type: e.target.value })}>
                {boot.storage_types.map((t) => <option key={t} value={t}>{t}</option>)}
              </Select>
            </Field>
            <Field label="Issue order">
              <Select style={{ ...S.inp, width: '100%' }} value={edit.valuation} onChange={(e) => set({ valuation: e.target.value })}>
                <option value="FIFO">FIFO — oldest receipt first</option>
                <option value="FEFO">FEFO — earliest expiry first</option>
              </Select>
            </Field>
          </div>
          <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', margin: '0.85rem 0', fontSize: '0.88rem' }}>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={edit.batch_tracked} onChange={(e) => set({ batch_tracked: e.target.checked })} /> Track batch numbers</label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={edit.expiry_tracked} onChange={(e) => set({ expiry_tracked: e.target.checked, batch_tracked: e.target.checked || edit.batch_tracked })} /> Track expiry dates</label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={edit.is_active} onChange={(e) => set({ is_active: e.target.checked })} /> Active</label>
          </div>
          {edit.has_movements && <p style={{ ...S.sub, marginTop: 0 }}>This item has stock movements, so its department and base unit are locked.</p>}

          <h4 style={{ margin: '0.75rem 0 0.4rem', fontSize: '0.92rem' }}>Other units</h4>
          <p style={{ ...S.sub, marginTop: 0 }}>For example, buy in CASE where 1 CASE = 24 {edit.base_uom}.</p>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead><tr><th style={S.th}>Unit</th><th style={{ ...S.th, ...S.num }}>= how many {edit.base_uom}</th><th style={S.th}>Buy in this unit</th><th style={S.th}>Issue in this unit</th><th style={S.th} /></tr></thead>
              <tbody>
                {edit.uoms.map((u, idx) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={idx}>
                    <td style={S.td}>
                      <Select style={S.inp} value={u.uom_code} onChange={(e) => set({ uoms: edit.uoms.map((x, i) => (i === idx ? { ...x, uom_code: e.target.value } : x)) })}>
                        <option value="">Choose…</option>
                        {boot.uoms.filter((m) => m.code !== edit.base_uom).map((m) => <option key={m.code} value={m.code}>{m.code}</option>)}
                      </Select>
                    </td>
                    <td style={S.td}><input type="number" min="0" step="any" style={{ ...S.inp, width: 110, textAlign: 'right' }} value={u.factor_to_base} onChange={(e) => set({ uoms: edit.uoms.map((x, i) => (i === idx ? { ...x, factor_to_base: e.target.value } : x)) })} /></td>
                    <td style={S.td}><input type="checkbox" checked={!!u.is_purchase_uom} onChange={(e) => set({ uoms: edit.uoms.map((x, i) => (i === idx ? { ...x, is_purchase_uom: e.target.checked } : x)) })} /></td>
                    <td style={S.td}><input type="checkbox" checked={!!u.is_issue_uom} onChange={(e) => set({ uoms: edit.uoms.map((x, i) => (i === idx ? { ...x, is_issue_uom: e.target.checked } : x)) })} /></td>
                    <td style={S.td}><button type="button" className="btn-muted btn-sm" onClick={() => set({ uoms: edit.uoms.filter((_, i) => i !== idx) })} aria-label="Remove unit">✕</button></td>
                  </tr>
                ))}
                {!edit.uoms.length && <tr><td colSpan={5} style={{ ...S.empty, padding: '0.75rem' }}>Only the base unit is used.</td></tr>}
              </tbody>
            </table>
          </div>
          <button type="button" className="btn-muted btn-sm" style={{ marginTop: '0.4rem' }} onClick={() => set({ uoms: [...edit.uoms, { uom_code: '', factor_to_base: '', is_purchase_uom: true, is_issue_uom: false }] })}>+ Add unit</button>

          <h4 style={{ margin: '1rem 0 0.4rem', fontSize: '0.92rem' }}>Stock levels by store <span style={{ fontWeight: 400, color: '#64748b' }}>(in {edit.base_uom})</span></h4>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead><tr><th style={S.th}>Store</th><th style={{ ...S.th, ...S.num }}>Minimum</th><th style={{ ...S.th, ...S.num }}>Reorder at</th><th style={{ ...S.th, ...S.num }}>Maximum</th><th style={{ ...S.th, ...S.num }}>Par</th></tr></thead>
              <tbody>
                {deptStores.map((s) => {
                  const l = levelFor(s.id);
                  return (
                    <tr key={s.id}>
                      <td style={S.td}>{s.store_name}</td>
                      {['min_qty', 'reorder_qty', 'max_qty', 'par_qty'].map((k) => (
                        <td key={k} style={{ ...S.td, ...S.num }}><input type="number" min="0" step="any" style={{ ...S.inp, width: 90, textAlign: 'right' }} value={l[k]} onChange={(e) => setLevel(s.id, { [k]: e.target.value })} /></td>
                      ))}
                    </tr>
                  );
                })}
                {!deptStores.length && <tr><td colSpan={5} style={{ ...S.empty, padding: '0.75rem' }}>{edit.department_id ? 'This department has no stores yet.' : 'Choose a department first.'}</td></tr>}
              </tbody>
            </table>
          </div>

          <Field label="Description" style={{ marginTop: '0.9rem' }}><textarea rows={2} style={{ ...S.inp, width: '100%', fontFamily: 'inherit' }} value={edit.description} onChange={(e) => set({ description: e.target.value })} /></Field>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
            <button type="button" className="btn-muted" onClick={() => setEdit(null)} disabled={saving}>Cancel</button>
            <button type="button" className="btn-primary" onClick={save} disabled={saving || !edit.department_id || !edit.item_name.trim()}>{saving ? 'Saving…' : 'Save item'}</button>
          </div>
        </Modal>
      )}
    </>
  );
}

// ── Masters: stores, categories, units, departments ───────────────────────
function Masters({ api, boot, onChanged }) {
  const [tab, setTab] = useState('stores');
  const [edit, setEdit] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const canEdit = boot.me.is_hotel_admin || boot.me.roles.includes('STORE_MANAGER');
  const deptName = (id) => boot.departments.find((d) => d.id === id)?.name || '—';

  const save = async () => {
    setSaving(true); setErr('');
    const paths = { stores: '/stores', categories: '/categories', uoms: '/uoms', departments: '/departments' };
    try {
      const path = edit.id ? `${paths[tab]}/${edit.id}` : paths[tab];
      await api(path, { method: edit.id ? 'PUT' : 'POST', body: edit });
      setMsg('Saved.');
      setEdit(null);
      onChanged();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const newRecord = {
    stores: () => ({ department_id: boot.departments[0]?.id || '', store_code: '', store_name: '', store_type: 'MAIN', storage_type: 'DRY', parent_store_id: '', location: '', is_active: true }),
    categories: () => ({ department_id: boot.departments[0]?.id || '', name: '', gl_account: '', is_active: true }),
    uoms: () => ({ code: '', name: '', decimals: 0, is_active: true }),
    departments: () => ({ code: '', name: '', cost_centre: '', sort_order: 9, is_active: true }),
  };
  const canEditTab = tab === 'departments' ? boot.me.is_hotel_admin : canEdit;
  const set = (patch) => setEdit((e) => ({ ...e, ...patch }));
  const statusPill = (on) => (on ? <span style={S.pill('#dcfce7', '#15803d')}>Active</span> : <span style={S.pill('#f1f5f9', '#64748b')}>Inactive</span>);

  return (
    <>
      <div style={{ display: 'flex', gap: '0.25rem', borderBottom: '1px solid #e2e8f0', marginBottom: '0.9rem', flexWrap: 'wrap' }}>
        {[['stores', 'Stores'], ['categories', 'Categories'], ['uoms', 'Units'], ['departments', 'Departments']].map(([k, l]) => (
          <button key={k} type="button" style={S.tab(tab === k)} onClick={() => { setTab(k); setEdit(null); setErr(''); setMsg(''); }}>{l}</button>
        ))}
      </div>
      {err && !edit && <div style={S.err}>{err}</div>}
      {msg && <div style={S.ok}>{msg}</div>}
      {canEditTab && <button type="button" className="btn-primary" style={{ marginBottom: '0.75rem' }} onClick={() => { setErr(''); setMsg(''); setEdit(newRecord[tab]()); }}>+ New</button>}

      <div style={S.tableWrap}>
        <table style={S.table}>
          {tab === 'stores' && (
            <>
              <thead><tr><th style={S.th}>Code</th><th style={S.th}>Store</th><th style={S.th}>Department</th><th style={S.th}>Type</th><th style={S.th}>Storage</th><th style={S.th}>Location</th><th style={S.th}>Status</th><th style={S.th} /></tr></thead>
              <tbody>{boot.stores.map((s) => (
                <tr key={s.id}><td style={{ ...S.td, fontFamily: 'monospace' }}>{s.store_code}</td><td style={{ ...S.td, fontWeight: 600 }}>{s.store_name}</td><td style={S.td}>{deptName(s.department_id)}</td><td style={S.td}>{s.store_type}</td><td style={S.td}>{s.storage_type}</td><td style={S.td}>{s.location || '—'}</td><td style={S.td}>{statusPill(s.is_active)}</td>
                  <td style={S.td}>{canEdit && <button type="button" className="btn-muted btn-sm" onClick={() => setEdit({ ...s, parent_store_id: s.parent_store_id || '', location: s.location || '' })}>Edit</button>}</td></tr>
              ))}</tbody>
            </>
          )}
          {tab === 'categories' && (
            <>
              <thead><tr><th style={S.th}>Category</th><th style={S.th}>Department</th><th style={S.th}>GL account</th><th style={S.th}>Status</th><th style={S.th} /></tr></thead>
              <tbody>{boot.categories.map((c) => (
                <tr key={c.id}><td style={{ ...S.td, fontWeight: 600 }}>{c.name}</td><td style={S.td}>{deptName(c.department_id)}</td><td style={S.td}>{c.gl_account || '—'}</td><td style={S.td}>{statusPill(c.is_active)}</td>
                  <td style={S.td}>{canEdit && <button type="button" className="btn-muted btn-sm" onClick={() => setEdit({ ...c, gl_account: c.gl_account || '' })}>Edit</button>}</td></tr>
              ))}
              {!boot.categories.length && <tr><td colSpan={5} style={S.empty}>No categories yet.</td></tr>}</tbody>
            </>
          )}
          {tab === 'uoms' && (
            <>
              <thead><tr><th style={S.th}>Code</th><th style={S.th}>Name</th><th style={{ ...S.th, ...S.num }}>Decimals</th><th style={S.th}>Status</th><th style={S.th} /></tr></thead>
              <tbody>{boot.uoms.map((u) => (
                <tr key={u.id}><td style={{ ...S.td, fontFamily: 'monospace', fontWeight: 600 }}>{u.code}</td><td style={S.td}>{u.name}</td><td style={{ ...S.td, ...S.num }}>{u.decimals}</td><td style={S.td}>{statusPill(u.is_active)}</td>
                  <td style={S.td}>{canEdit && <button type="button" className="btn-muted btn-sm" onClick={() => setEdit({ ...u })}>Edit</button>}</td></tr>
              ))}</tbody>
            </>
          )}
          {tab === 'departments' && (
            <>
              <thead><tr><th style={S.th}>Code</th><th style={S.th}>Department</th><th style={S.th}>Cost centre</th><th style={{ ...S.th, ...S.num }}>Order</th><th style={S.th}>Status</th><th style={S.th} /></tr></thead>
              <tbody>{boot.departments.map((d) => (
                <tr key={d.id}><td style={{ ...S.td, fontFamily: 'monospace', fontWeight: 600 }}>{d.code}</td><td style={S.td}>{d.name}</td><td style={S.td}>{d.cost_centre || '—'}</td><td style={{ ...S.td, ...S.num }}>{d.sort_order}</td><td style={S.td}>{statusPill(d.is_active)}</td>
                  <td style={S.td}>{boot.me.is_hotel_admin && <button type="button" className="btn-muted btn-sm" onClick={() => setEdit({ ...d, cost_centre: d.cost_centre || '' })}>Edit</button>}</td></tr>
              ))}</tbody>
            </>
          )}
        </table>
      </div>

      {edit && (
        <Modal title={`${edit.id ? 'Edit' : 'New'} ${{ stores: 'store', categories: 'category', uoms: 'unit', departments: 'department' }[tab]}`} onClose={() => setEdit(null)} width={620}>
          {err && <div style={S.err}>{err}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem' }}>
            {tab === 'stores' && (
              <>
                <Field label="Department *">
                  <Select style={{ ...S.inp, width: '100%' }} value={edit.department_id} onChange={(e) => set({ department_id: e.target.value })}>
                    {boot.departments.filter((d) => d.is_active).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </Select>
                </Field>
                <Field label="Store code *"><input style={{ ...S.inp, width: '100%' }} value={edit.store_code} placeholder="e.g. HK-FLOOR3" onChange={(e) => set({ store_code: e.target.value })} /></Field>
                <Field label="Store name *" style={{ gridColumn: '1 / -1' }}><input style={{ ...S.inp, width: '100%' }} value={edit.store_name} onChange={(e) => set({ store_name: e.target.value })} /></Field>
                <Field label="Type">
                  <Select style={{ ...S.inp, width: '100%' }} value={edit.store_type} onChange={(e) => set({ store_type: e.target.value })}>
                    {boot.store_types.map((t) => <option key={t} value={t}>{t}</option>)}
                  </Select>
                </Field>
                <Field label="Storage">
                  <Select style={{ ...S.inp, width: '100%' }} value={edit.storage_type} onChange={(e) => set({ storage_type: e.target.value })}>
                    {boot.storage_types.map((t) => <option key={t} value={t}>{t}</option>)}
                  </Select>
                </Field>
                <Field label="Parent store">
                  <Select style={{ ...S.inp, width: '100%' }} value={edit.parent_store_id} onChange={(e) => set({ parent_store_id: e.target.value })}>
                    <option value="">None</option>
                    {boot.stores.filter((s) => s.id !== edit.id && s.department_id === edit.department_id).map((s) => <option key={s.id} value={s.id}>{s.store_name}</option>)}
                  </Select>
                </Field>
                <Field label="Location"><input style={{ ...S.inp, width: '100%' }} value={edit.location} placeholder="e.g. Basement B1" onChange={(e) => set({ location: e.target.value })} /></Field>
              </>
            )}
            {tab === 'categories' && (
              <>
                <Field label="Department *">
                  <Select style={{ ...S.inp, width: '100%' }} value={edit.department_id} disabled={!!edit.id} onChange={(e) => set({ department_id: e.target.value })}>
                    {boot.departments.filter((d) => d.is_active).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </Select>
                </Field>
                <Field label="Category name *"><input style={{ ...S.inp, width: '100%' }} value={edit.name} onChange={(e) => set({ name: e.target.value })} /></Field>
                <Field label="GL account"><input style={{ ...S.inp, width: '100%' }} value={edit.gl_account} placeholder="For accounting export" onChange={(e) => set({ gl_account: e.target.value })} /></Field>
              </>
            )}
            {tab === 'uoms' && (
              <>
                <Field label="Code *"><input style={{ ...S.inp, width: '100%' }} value={edit.code} disabled={!!edit.id} placeholder="e.g. TRAY" onChange={(e) => set({ code: e.target.value })} /></Field>
                <Field label="Name *"><input style={{ ...S.inp, width: '100%' }} value={edit.name} onChange={(e) => set({ name: e.target.value })} /></Field>
                <Field label="Decimal places"><input type="number" min="0" max="4" style={{ ...S.inp, width: '100%' }} value={edit.decimals} onChange={(e) => set({ decimals: e.target.value })} /></Field>
              </>
            )}
            {tab === 'departments' && (
              <>
                <Field label="Code *"><input style={{ ...S.inp, width: '100%' }} value={edit.code} disabled={!!edit.id} placeholder="e.g. SPA" onChange={(e) => set({ code: e.target.value })} /></Field>
                <Field label="Name *"><input style={{ ...S.inp, width: '100%' }} value={edit.name} onChange={(e) => set({ name: e.target.value })} /></Field>
                <Field label="Cost centre"><input style={{ ...S.inp, width: '100%' }} value={edit.cost_centre} onChange={(e) => set({ cost_centre: e.target.value })} /></Field>
                <Field label="Display order"><input type="number" style={{ ...S.inp, width: '100%' }} value={edit.sort_order} onChange={(e) => set({ sort_order: e.target.value })} /></Field>
              </>
            )}
          </div>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: '0.85rem', fontSize: '0.88rem' }}><input type="checkbox" checked={edit.is_active !== false} onChange={(e) => set({ is_active: e.target.checked })} /> Active</label>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
            <button type="button" className="btn-muted" onClick={() => setEdit(null)} disabled={saving}>Cancel</button>
            <button type="button" className="btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </Modal>
      )}
    </>
  );
}

// ── Settings, user access, override register ──────────────────────────────
// ── Food Inventory → Stores (once per property) ──────────────────────────
function FoodMigration({ api, onChanged }) {
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState('');
  const [result, setResult] = useState(null);
  const load = useCallback(() => { setErr(''); api('/migrate/food').then(setInfo).catch((e) => setErr(e.message)); }, [api]);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setBusy(true); setErr('');
    try {
      const r = await api('/migrate/food', { method: 'POST', body: { confirm } });
      setResult(r.summary); setConfirm(''); load(); onChanged();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const Row = ({ label, value, warn }) => (
    <tr><td style={S.td}>{label}</td><td style={{ ...S.td, ...S.num, fontWeight: 700, color: warn ? '#b45309' : undefined }}>{value}</td></tr>
  );
  const summaryRows = (x) => (
    <table style={S.table}><tbody>
      <Row label="Stores created" value={x.stores} />
      <Row label="Items created" value={x.items} />
      <Row label="Categories / units created" value={`${x.categories} / ${x.units}`} />
      <Row label="History rows brought over" value={x.history_rows} />
      <Row label="Stock lines" value={x.stock_lines} />
      <Row label="Batches (+ balancing batches)" value={`${x.batches} (+${x.gap_batches})`} />
      <Row label="Ledger balancing entries" value={x.ledger_fixes} />
      <Row label="Negative balances set to zero" value={x.negatives_zeroed?.length || 0} warn={x.negatives_zeroed?.length > 0} />
      {x.renamed_codes?.length > 0 && <Row label="Item codes renamed (already used)" value={x.renamed_codes.join(', ')} warn />}
    </tbody></table>
  );

  if (!info) return err ? <div style={S.err}>{err}</div> : <div style={S.empty}>Loading…</div>;
  const c = info.counts || {};
  return (
    <div style={{ ...S.card, maxWidth: 820 }}>
      <h4 style={{ margin: '0 0 0.4rem' }}>Move Food Inventory into Stores</h4>
      <p style={{ ...S.sub, marginTop: 0 }}>
        A one-time move for this property. Food stores, items, categories and units are created under F&amp;B; every Food Inventory stock movement is copied into the item ledger;
        current stock comes over with its batches, expiry dates and cost. Afterwards Food Inventory is read-only here — purchases, receipts and issues are done in Stores.
      </p>
      {err && <div style={S.err}>{err}</div>}
      {result && <div style={S.ok}>Food Inventory has been moved into Stores.</div>}
      {info.done ? (
        <>
          <div style={{ ...S.ok, background: '#f8fafc', borderColor: '#e2e8f0', color: '#334155' }}>
            Moved on {new Date(info.done.food_migrated_at).toLocaleString('en-IN')} by {info.done.food_migrated_by || '—'}.
          </div>
          {info.done.food_migration_summary && <div style={S.tableWrap}>{summaryRows(info.done.food_migration_summary)}</div>}
        </>
      ) : !info.available ? (
        <div style={S.empty}>There is no Food Inventory data on this server.</div>
      ) : (
        <>
          <div style={S.tableWrap}>
            <table style={S.table}><tbody>
              <Row label="Food stores" value={c.stores} />
              <Row label="Items (active)" value={`${c.items} (${c.active_items})`} />
              <Row label="Items with stock / stock value" value={`${c.stock_lines} / ${fmtMoney(c.stock_value)}`} />
              <Row label="Open batches" value={c.open_batches} />
              <Row label="Stock movements (history)" value={c.transactions} />
              <Row label="Negative balances (will come over as zero)" value={c.negative_lines} warn={c.negative_lines > 0} />
              <Row label="Item codes already used in Stores (will get an F- prefix)" value={c.code_clashes} warn={c.code_clashes > 0} />
              <Row label="Open food purchase orders" value={c.open_pos} warn={c.open_pos > 0} />
              <Row label="Open store requisitions" value={c.open_requisitions} warn={c.open_requisitions > 0} />
            </tbody></table>
          </div>
          {(c.open_pos > 0 || c.open_requisitions > 0) && (
            <div style={{ ...S.err, background: '#fffbeb', borderColor: '#fcd34d', color: '#92400e', marginTop: '0.75rem' }}>
              Open food purchase orders and requisitions cannot be completed in Food Inventory after the move. Receive / issue them first, or raise them again in Stores.
            </div>
          )}
          <p style={{ ...S.sub }}>Do this at a quiet time (after the night audit). Everything happens in one step — if anything fails, nothing is changed.</p>
          <div style={S.bar}>
            <Field label='Type MIGRATE to confirm'><input style={{ ...S.inp, width: 160 }} value={confirm} onChange={(e) => setConfirm(e.target.value)} /></Field>
            <button type="button" className="btn-primary" onClick={run} disabled={busy || confirm !== 'MIGRATE'}>{busy ? 'Moving… (do not close this page)' : 'Move Food Inventory now'}</button>
          </div>
        </>
      )}
      {result && <div style={{ ...S.tableWrap, marginTop: '0.75rem' }}>{summaryRows(result)}</div>}
    </div>
  );
}

function SettingsAccess({ api, boot, onChanged }) {
  const isHotelAdmin = boot.me.is_hotel_admin;
  const canSeeOverrides = isHotelAdmin || boot.me.roles.includes('STORE_MANAGER') || boot.me.roles.includes('COST_CONTROLLER');
  const [tab, setTab] = useState(isHotelAdmin ? 'access' : 'overrides');
  const [users, setUsers] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [edit, setEdit] = useState(null);
  const [settings, setSettings] = useState(() => ({
    doc_prefixes: boot.settings?.doc_prefixes || {},
    grn_tolerance_pct: boot.settings?.grn_tolerance_pct ?? 0,
    auto_pr_from_reorder: Boolean(boot.settings?.auto_pr_from_reorder),
    indent_approval: Boolean(boot.settings?.indent_approval),
  }));
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setErr('');
    if (tab === 'access' && isHotelAdmin) api('/access').then(setUsers).catch((e) => setErr(e.message));
    if (tab === 'overrides' && canSeeOverrides) api('/override-log').then(setOverrides).catch((e) => setErr(e.message));
  }, [api, tab, isHotelAdmin, canSeeOverrides]);

  const saveAccess = async () => {
    setSaving(true); setErr('');
    try {
      await api(`/access/${edit.user_id}`, { method: 'PUT', body: { roles: edit.roles, department_ids: edit.department_ids, store_ids: edit.store_ids, is_active: true } });
      setMsg(`Stores access saved for ${edit.username}.`);
      setEdit(null);
      setUsers(await api('/access'));
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const saveSettings = async () => {
    setSaving(true); setErr(''); setMsg('');
    try {
      await api('/settings', { method: 'PUT', body: settings });
      setMsg('Settings saved.');
      onChanged();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const toggle = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  const deptNames = (ids) => (ids?.length ? ids.map((id) => boot.departments.find((d) => d.id === id)?.name).filter(Boolean).join(', ') : 'All');
  const storeNames = (ids) => (ids?.length ? ids.map((id) => boot.stores.find((s) => s.id === id)?.store_name).filter(Boolean).join(', ') : 'All');
  const DOC_TYPES = [['OPENING', 'OPN'], ['INDENT', 'IND'], ['PR', 'PR'], ['PO', 'PO'], ['GRN', 'GRN'], ['ISSUE', 'ISS'], ['RETURN', 'RET'], ['TRANSFER', 'TRF'], ['ADJUST', 'ADJ'], ['WASTAGE', 'WST'], ['COUNT', 'CNT'], ['PURCHASE_RETURN', 'PRN'], ['VENDOR_BILL', 'VB'], ['PAYMENT', 'PAY']];

  return (
    <>
      <div style={{ display: 'flex', gap: '0.25rem', borderBottom: '1px solid #e2e8f0', marginBottom: '0.9rem', flexWrap: 'wrap' }}>
        {isHotelAdmin && <button type="button" style={S.tab(tab === 'access')} onClick={() => { setTab('access'); setMsg(''); }}>User access</button>}
        {isHotelAdmin && <button type="button" style={S.tab(tab === 'settings')} onClick={() => { setTab('settings'); setMsg(''); }}>Settings</button>}
        {isHotelAdmin && <button type="button" style={S.tab(tab === 'rules')} onClick={() => { setTab('rules'); setMsg(''); }}>Approval rules</button>}
        {isHotelAdmin && <button type="button" style={S.tab(tab === 'food')} onClick={() => { setTab('food'); setMsg(''); }}>Food Inventory move</button>}
        {isHotelAdmin && <button type="button" style={S.tab(tab === 'modules')} onClick={() => { setTab('modules'); setMsg(''); }}>Housekeeping &amp; Maintenance</button>}
        {canSeeOverrides && <button type="button" style={S.tab(tab === 'overrides')} onClick={() => { setTab('overrides'); setMsg(''); }}>Override register</button>}
      </div>
      {err && !edit && <div style={S.err}>{err}</div>}
      {msg && <div style={S.ok}>{msg}</div>}
      {!isHotelAdmin && !canSeeOverrides && <div style={S.empty}>Only Hotel Admin can manage Stores settings.</div>}

      {tab === 'access' && isHotelAdmin && (
        <>
          <p style={{ ...S.sub, marginTop: 0, marginBottom: '0.75rem' }}>Hotel Admin and Master / Super Admin always have full access. Other users need the Stores box ticked under Users → Module Access. Their Stores roles come from their Food / Accounting boxes there (none = Requester) until you set roles here; limit them to certain departments or stores if needed.</p>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead><tr><th style={S.th}>User</th><th style={S.th}>Stores roles</th><th style={S.th}>Departments</th><th style={S.th}>Stores</th><th style={S.th} /></tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id}>
                    <td style={S.td}>
                      <div style={{ fontWeight: 600 }}>{u.username}</div><div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{u.role}</div>
                      {!u.can_stores && !u.can_hotel_admin && <div style={{ fontSize: '0.72rem', color: '#b45309' }}>Stores not ticked in Users — no access until it is</div>}
                    </td>
                    <td style={S.td}>
                      {u.is_active && u.roles?.length ? u.roles.map((r) => <span key={r} style={{ ...S.pill('#e0e7ff', '#3730a3'), marginRight: 4 }}>{ROLE_LABELS[r] || r}</span>) : <span style={{ color: '#94a3b8' }}>No access</span>}
                      {u.is_default && <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>Default from Users checkboxes — Edit to change</div>}
                    </td>
                    <td style={S.td}>{u.roles?.length ? deptNames(u.department_ids) : '—'}</td>
                    <td style={S.td}>{u.roles?.length ? storeNames(u.store_ids) : '—'}</td>
                    <td style={S.td}><button type="button" className="btn-muted btn-sm" onClick={() => { setErr(''); setEdit({ user_id: u.user_id, username: u.username, roles: u.is_active ? (u.roles || []) : [], department_ids: u.department_ids || [], store_ids: u.store_ids || [] }); }}>Edit</button></td>
                  </tr>
                ))}
                {!users.length && <tr><td colSpan={5} style={S.empty}>No users are linked to this property.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'settings' && isHotelAdmin && (
        <div style={{ ...S.card, maxWidth: 760 }}>
          <h4 style={{ margin: '0 0 0.5rem' }}>Document number prefixes</h4>
          <p style={{ ...S.sub, marginTop: 0 }}>Numbers run per financial year, e.g. GRN/2026-27/0001.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '0.6rem', marginBottom: '1rem' }}>
            {DOC_TYPES.map(([k, def]) => (
              <Field key={k} label={k.replace('_', ' ')}><input style={{ ...S.inp, width: '100%' }} value={settings.doc_prefixes[k] ?? ''} placeholder={def} onChange={(e) => setSettings((s) => ({ ...s, doc_prefixes: { ...s.doc_prefixes, [k]: e.target.value } }))} /></Field>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.75rem' }}>
            <Field label="GRN over-receipt tolerance %"><input type="number" min="0" max="100" step="any" style={{ ...S.inp, width: '100%' }} value={settings.grn_tolerance_pct} onChange={(e) => setSettings((s) => ({ ...s, grn_tolerance_pct: e.target.value }))} /></Field>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.88rem', paddingTop: '1.1rem' }}><input type="checkbox" checked={settings.indent_approval} onChange={(e) => setSettings((s) => ({ ...s, indent_approval: e.target.checked }))} /> Indents need Department Head approval</label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.88rem', paddingTop: '1.1rem' }}><input type="checkbox" checked={settings.auto_pr_from_reorder} onChange={(e) => setSettings((s) => ({ ...s, auto_pr_from_reorder: e.target.checked }))} /> Suggest purchase requisitions from reorder levels</label>
          </div>
          <div style={{ ...S.ok, background: '#f8fafc', borderColor: '#e2e8f0', color: '#334155', marginTop: '1rem' }}>Stock can never be issued beyond what is available. Only a Master / Super Admin can authorise an exception, and every exception is listed in the override register.</div>
          <button type="button" className="btn-primary" onClick={saveSettings} disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</button>
        </div>
      )}

      {tab === 'rules' && isHotelAdmin && <ApprovalRules api={api} boot={boot} />}
      {tab === 'food' && isHotelAdmin && <FoodMigration api={api} onChanged={onChanged} />}
      {tab === 'modules' && isHotelAdmin && <ModuleMoves api={api} boot={boot} onChanged={onChanged} />}

      {tab === 'overrides' && canSeeOverrides && (
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead><tr><th style={S.th}>When</th><th style={S.th}>Item</th><th style={S.th}>Store</th><th style={S.th}>Document</th><th style={{ ...S.th, ...S.num }}>Requested</th><th style={{ ...S.th, ...S.num }}>Available</th><th style={{ ...S.th, ...S.num }}>Overridden</th><th style={S.th}>Reason</th><th style={S.th}>Authorised by</th></tr></thead>
            <tbody>
              {overrides.map((o) => (
                <tr key={o.id}>
                  <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{new Date(o.authorised_at).toLocaleString('en-IN')}</td>
                  <td style={S.td}>{o.item_name}<div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{o.item_code}</div></td>
                  <td style={S.td}>{o.store_name}</td><td style={S.td}>{o.doc_no || '—'}</td>
                  <td style={{ ...S.td, ...S.num }}>{fmtQty(o.requested_qty)} {o.base_uom}</td><td style={{ ...S.td, ...S.num }}>{fmtQty(o.available_qty)}</td>
                  <td style={{ ...S.td, ...S.num, fontWeight: 700, color: '#c2410c' }}>{fmtQty(o.override_qty)}</td>
                  <td style={S.td}>{o.reason}</td><td style={S.td}>{o.authorised_by}</td>
                </tr>
              ))}
              {!overrides.length && <tr><td colSpan={9} style={S.empty}>No overrides. Stock has never been issued beyond what was available.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {edit && (
        <Modal title={`Stores access — ${edit.username}`} onClose={() => setEdit(null)} width={680}>
          {err && <div style={S.err}>{err}</div>}
          <h4 style={{ margin: '0 0 0.4rem', fontSize: '0.9rem' }}>Roles</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '0.35rem', marginBottom: '0.9rem' }}>
            {boot.roles.map((r) => (
              <label key={r} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.88rem' }}><input type="checkbox" checked={edit.roles.includes(r)} onChange={() => setEdit((e) => ({ ...e, roles: toggle(e.roles, r) }))} /> {ROLE_LABELS[r] || r}</label>
            ))}
          </div>
          <h4 style={{ margin: '0 0 0.2rem', fontSize: '0.9rem' }}>Departments</h4>
          <p style={{ ...S.sub, marginTop: 0 }}>Leave all unticked for every department.</p>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.9rem' }}>
            {boot.departments.map((d) => (
              <label key={d.id} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.88rem' }}><input type="checkbox" checked={edit.department_ids.includes(d.id)} onChange={() => setEdit((e) => ({ ...e, department_ids: toggle(e.department_ids, d.id) }))} /> {d.name}</label>
            ))}
          </div>
          <h4 style={{ margin: '0 0 0.2rem', fontSize: '0.9rem' }}>Stores</h4>
          <p style={{ ...S.sub, marginTop: 0 }}>Leave all unticked for every store in the chosen departments.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '0.35rem' }}>
            {boot.stores.filter((s) => !edit.department_ids.length || edit.department_ids.includes(s.department_id)).map((s) => (
              <label key={s.id} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.88rem' }}><input type="checkbox" checked={edit.store_ids.includes(s.id)} onChange={() => setEdit((e) => ({ ...e, store_ids: toggle(e.store_ids, s.id) }))} /> {s.store_name}</label>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.1rem' }}>
            <button type="button" className="btn-muted" onClick={() => setEdit(null)} disabled={saving}>Cancel</button>
            <button type="button" className="btn-primary" onClick={saveAccess} disabled={saving}>{saving ? 'Saving…' : edit.roles.length ? 'Save access' : 'Remove access'}</button>
          </div>
        </Modal>
      )}
    </>
  );
}

// ── Excel import (items or opening stock): check → review → import ────────
function ImportDialog({ kind, io, boot, deptFilter, onClose, onDone }) {
  const isItems = kind === 'items';
  const [file, setFile] = useState(null);
  const [deptId, setDeptId] = useState(deptFilter || '');
  const [mode, setMode] = useState('skip');
  const firstStore = boot.stores.find((s) => s.is_active && (!deptFilter || s.department_id === deptFilter));
  const [storeId, setStoreId] = useState(firstStore?.id || '');
  const [docDate, setDocDate] = useState(today());
  const [result, setResult] = useState(null);
  const [onlyProblems, setOnlyProblems] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const path = isItems ? '/import/items' : '/import/opening';
  const run = async (commit) => {
    if (!file) { setErr('Choose a file first'); return; }
    setBusy(true); setErr('');
    try {
      const form = new FormData();
      form.append('file', file);
      if (isItems) { form.append('department_id', deptId); form.append('mode', mode); } else { form.append('store_id', storeId); form.append('doc_date', docDate); }
      if (commit) form.append('commit', '1');
      const data = await io.upload(path, form);
      setResult(data);
      if (!data.ok && data.error) setErr(data.error);
      if (data.committed) {
        const s = data.summary;
        onDone(isItems
          ? `Imported ${s.created} new item(s)${s.updated ? `, updated ${s.updated}` : ''}${s.skip ? `, skipped ${s.skip} existing` : ''}.`
          : `Posted ${(data.docs || []).map((d) => d.doc_no).join(', ')} — ${s.post} line(s), ${fmtMoney(s.value)}.`);
        onClose();
      }
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const template = () => io.download(`${path}/template`, isItems ? 'stores_item_import_template.xlsx' : 'stores_opening_stock_template.xlsx').catch((e) => setErr(e.message));

  const s = result?.summary;
  const rows = (result?.rows || []).filter((r) => !onlyProblems || r.issues.length);
  const canCommit = s && s.errors === 0 && (isItems ? s.create + s.update > 0 : s.post > 0);
  const actionPill = (a) => ({
    CREATE: S.pill('#dcfce7', '#15803d'), UPDATE: S.pill('#dbeafe', '#1d4ed8'), POST: S.pill('#dcfce7', '#15803d'), SKIP: S.pill('#f1f5f9', '#64748b'),
  }[a]);

  return (
    <Modal title={isItems ? 'Import items from Excel' : 'Import opening stock from Excel'} onClose={onClose} width={1100}>
      <p style={{ ...S.sub, marginTop: 0 }}>
        {isItems
          ? "Upload the template or the client's own item list (.xlsx, .xls or .csv). Item code, description and unit are enough; pack sizes like \"[ 100 GM ]\" become purchase units. Nothing is saved until you press Import."
          : 'Upload item codes with counted quantities. Items must already exist. One opening document is posted per store. Nothing is posted until you press Post.'}
        {' '}<button type="button" className="btn-muted btn-sm" onClick={template}>Download template</button>
      </p>
      <div style={S.bar}>
        <Field label="File">
          <input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); setErr(''); }} style={{ ...S.inp, width: 280 }} />
        </Field>
        {isItems ? (
          <>
            <Field label="Department (when the file has none)">
              <Select style={{ ...S.inp, minWidth: 190 }} value={deptId} onChange={(e) => { setDeptId(e.target.value); setResult(null); }}>
                <option value="">From the file</option>
                {boot.departments.filter((d) => d.is_active).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </Select>
            </Field>
            <Field label="Codes that already exist">
              <Select style={{ ...S.inp, minWidth: 170 }} value={mode} onChange={(e) => { setMode(e.target.value); setResult(null); }}>
                <option value="skip">Skip them</option>
                <option value="update">Update them</option>
              </Select>
            </Field>
          </>
        ) : (
          <>
            <Field label="Store (when the file has none)">
              <Select style={{ ...S.inp, minWidth: 200 }} value={storeId} onChange={(e) => { setStoreId(e.target.value); setResult(null); }}>
                <option value="">From the file</option>
                {boot.stores.filter((st) => st.is_active).map((st) => <option key={st.id} value={st.id}>{st.store_name}</option>)}
              </Select>
            </Field>
            <Field label="Stock date"><input type="date" style={S.inp} value={docDate} onChange={(e) => { setDocDate(e.target.value); setResult(null); }} /></Field>
          </>
        )}
        <button type="button" className="btn-primary" onClick={() => run(false)} disabled={busy || !file}>{busy && !result ? 'Checking…' : 'Check file'}</button>
      </div>
      {err && <div style={S.err}>{err}</div>}

      {s && (
        <>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', margin: '0.25rem 0 0.75rem' }}>
            <span style={S.pill('#f1f5f9', '#334155')}>{s.total} rows</span>
            {isItems ? (
              <>
                <span style={S.pill('#dcfce7', '#15803d')}>{s.create} new</span>
                {s.update > 0 && <span style={S.pill('#dbeafe', '#1d4ed8')}>{s.update} update</span>}
                {s.skip > 0 && <span style={S.pill('#f1f5f9', '#64748b')}>{s.skip} skip</span>}
              </>
            ) : (
              <>
                <span style={S.pill('#dcfce7', '#15803d')}>{s.post} to post · {fmtMoney(s.value)}</span>
                {s.skip > 0 && <span style={S.pill('#f1f5f9', '#64748b')}>{s.skip} skipped (qty 0)</span>}
                {s.stores?.length > 0 && <span style={S.sub}>into {s.stores.join(', ')}</span>}
              </>
            )}
            <span style={s.errors ? S.pill('#fee2e2', '#b91c1c') : S.pill('#dcfce7', '#15803d')}>{s.errors ? `${s.errors} with problems` : 'No problems'}</span>
            {s.errors > 0 && <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.85rem' }}><input type="checkbox" checked={onlyProblems} onChange={(e) => setOnlyProblems(e.target.checked)} /> Show only problems</label>}
          </div>
          {isItems && (s.new_categories?.length > 0 || s.new_units?.length > 0) && (
            <p style={{ ...S.sub, marginTop: 0 }}>
              {s.new_categories?.length > 0 && <>Will create {s.new_categories.length} categor{s.new_categories.length === 1 ? 'y' : 'ies'}: {s.new_categories.join(', ')}. </>}
              {s.new_units?.length > 0 && <>Will create unit{s.new_units.length === 1 ? '' : 's'}: {s.new_units.join(', ')}.</>}
            </p>
          )}
          <div style={{ ...S.tableWrap, maxHeight: '48vh', overflowY: 'auto' }}>
            <table style={S.table}>
              <thead>
                {isItems ? (
                  <tr><th style={S.th}>Row</th><th style={S.th}>Action</th><th style={S.th}>Code</th><th style={S.th}>Item</th><th style={S.th}>Department / category</th><th style={S.th}>Unit</th><th style={S.th}>Buy in</th><th style={S.th}>Storage</th><th style={S.th}>Problems / notes</th></tr>
                ) : (
                  <tr><th style={S.th}>Row</th><th style={S.th}>Action</th><th style={S.th}>Code</th><th style={S.th}>Item</th><th style={S.th}>Store</th><th style={{ ...S.th, ...S.num }}>Qty</th><th style={{ ...S.th, ...S.num }}>Rate</th><th style={{ ...S.th, ...S.num }}>Amount</th><th style={S.th}>Batch / expiry</th><th style={S.th}>Problems / notes</th></tr>
                )}
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.row} style={r.issues.length ? { background: '#fef2f2' } : undefined}>
                    <td style={S.td}>{r.row}</td>
                    <td style={S.td}><span style={actionPill(r.action)}>{r.action}</span></td>
                    <td style={{ ...S.td, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{r.item_code || <span style={{ color: '#94a3b8' }}>auto</span>}</td>
                    <td style={S.td}>{r.item_name}{isItems && r.is_active === false ? <> <span style={S.pill('#f1f5f9', '#64748b')}>Inactive</span></> : null}</td>
                    {isItems ? (
                      <>
                        <td style={S.td}>{r.department || '—'}{r.category ? <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{r.category}</div> : null}</td>
                        <td style={S.td}>{r.base_uom}</td>
                        <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{r.purchase || '—'}</td>
                        <td style={S.td}>{r.storage_type} · {r.valuation}</td>
                      </>
                    ) : (
                      <>
                        <td style={S.td}>{r.store || '—'}</td>
                        <td style={{ ...S.td, ...S.num }}>{r.qty != null ? `${fmtQty(r.qty)} ${r.unit || ''}` : '—'}</td>
                        <td style={{ ...S.td, ...S.num }}>{fmtMoney(r.rate)}</td>
                        <td style={{ ...S.td, ...S.num }}>{fmtMoney(r.amount)}</td>
                        <td style={S.td}>{r.batch_no || '—'}{r.expiry_date ? ` · ${fmtDate(r.expiry_date)}` : ''}</td>
                      </>
                    )}
                    <td style={{ ...S.td, fontSize: '0.78rem' }}>
                      {r.issues.map((i) => <div key={i} style={{ color: '#b91c1c' }}>✕ {i}</div>)}
                      {r.notes.map((n) => <div key={n} style={{ color: '#64748b' }}>{n}</div>)}
                    </td>
                  </tr>
                ))}
                {!rows.length && <tr><td colSpan={10} style={S.empty}>No rows to show.</td></tr>}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.9rem' }}>
            <span style={S.sub}>{s.errors ? 'Fix the rows marked ✕ in your file and check it again.' : 'Everything looks right.'}</span>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="button" className="btn-muted" onClick={onClose} disabled={busy}>Cancel</button>
              <button type="button" className="btn-primary" onClick={() => run(true)} disabled={busy || !canCommit}>
                {busy ? 'Working…' : isItems ? `Import ${s.create + s.update} item${s.create + s.update === 1 ? '' : 's'}` : `Post ${s.post} line${s.post === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
